import {css, html, LitElement} from 'lit';
import {customElement} from 'lit/decorators/custom-element.js';
import {property} from 'lit/decorators/property.js';
import {classMap} from 'lit/directives/class-map.js';
import {connect} from 'pwa-helpers';
import Plotly from 'plotly.js-dist-min';
import {msg} from '@lit/localize';
import {when} from 'lit/directives/when.js';

import store from '../../store';
import {
	selectColourTemperature,
	selectSolarDimmProfilePlotDataBySeason,
	selectSolarLuminarCountValue,
	selectSolarLuminarPower,
	selectSolarMaxLumen, selectSolarPVMaxInput, selectSolarPowerRequirementsBySeason, selectMaxUsableBatteryCapacity
} from '../solarSelectors';
import {selectLightDistribution} from '../../selectors';
import {
	selectSolarMaxPower,
	selectSolarMaxPower1,
	selectSolarMaxPower2,
	selectSolarMaxPower3,
	selectSolarMaxPower4,
	selectSolarPowerLimit
} from '../solarSelectorsCalculations';
import {calculateLumFromPower} from '../../util/calculateLumFromPower';

@customElement('solar-dimm-profile')
export class solarDimmProfile extends connect(store)(LitElement) {

	static styles = css`
		:host {
			display: flex;
			flex-direction: column;
			width: 100%;
		}

		.solarDimmProfile {
			display: flex;
			flex-direction: row;
			justify-content: space-between;
		}

		@media (max-width: 1366px) {
			.solarDimmProfile {
				flex-direction: column;
			}
		}

		.values {
			font: var(--font-widget-title);
			color: var(--color-white);
			line-height: 1.4;
		}

		.label {
			color: var(--color-grey4);
		}

		.debug {
			margin: 0 0 1rem 0;
			font: var(--font-widget-title);
			color: #006000;
		}

		.warn {
			color: #c16d00;
		}

		.overflow {
			color: #f10202
		}

		.debug {
			display: none;
		}

		.debug.visible {
			display: block;
		}
	`;

	plotOptions = {
		showSendToCloud: false,
		displayModeBar: false,
		showlegend: false,
		staticPlot: true,
		scrollZoom: false
	};
	plotLayout = {
		xaxis: {
			mirror: false,
			type: 'category',
			showgrid: false,
			color: '#939393',
			tickcolor: '#939393',
			ticks: 'outside',
			ticklen: 1,
			showticklabels: true,
			tickvals: ['12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '00', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'],
			ticktext: ['', '12h', '', '', '', '', '', '', '', '', '', '', '0h', '', '', '', '', '', '', '', '', '', '12h', '']
		},
		yaxis: {
			showgrid: false,
			tickcolor: '#fff',
			ticks: '',
			showticklabels: false,
			autorange: false,
			range: [0, 0.5]
		},
		width: 168,
		height: 80,
		margin: {
			t: 0,
			b: 30,
			r: 0,
			l: 0
		},
		autosize: false,
		font: {
			color: '#fff',
		},
		paper_bgcolor: 'transparent',
		plot_bgcolor: 'transparent',
	};
	plotData = null;

	@property({type: String})
	season = 'winter';

	@property({type: Number})
	generators = 1;

	@property({type: String})
	title = '';

	constructor() {
		super();

		store.subscribe(() => this.requestUpdate());
	}
	stateChanged(state) {
		this.state = state;
		this.lang = state.status.lang;
		this.solar = state.solar.data;
		this.translations = state.productData.translations;
		this.debug = state.status.debug;

		this.plotData = selectSolarDimmProfilePlotDataBySeason(state)[this.season];
		this.powerRequirement = selectSolarPowerRequirementsBySeason(state)[this.season];

		//this.maxPVInput = selectSolarPVMaxInput(state)[this.generators - 1][this.season];
		//this.maxUsableBatteryCapcity = selectMaxUsableBatteryCapacity(state);
		//this.usableInput = Math.min(this.maxPVInput, this.maxUsableBatteryCapcity);
		switch(this.generators) {
			case 1: this.usableInput = selectSolarMaxPower1(state)[this.season]; break;
			case 2: this.usableInput = selectSolarMaxPower2(state)[this.season]; break;
			case 3: this.usableInput = selectSolarMaxPower3(state)[this.season]; break;
			case 4: this.usableInput = selectSolarMaxPower4(state)[this.season]; break;
		}

		this.usedInputFactor = this.powerRequirement / this.usableInput;
		this.restrictionDimmingFactor = Math.max(0, Math.min(1, 1 / this.usedInputFactor));
		this.lumCount = selectSolarLuminarCountValue(state) ?? 1;

		//this.luminarPower = selectSolarLuminarPower(state) * this.restrictionDimmingFactor;
		this.luminarPower = this.usableInput; //selectSolarLuminarPower(state) * this.restrictionDimmingFactor;
		this.adjustedLum = Math.round(calculateLumFromPower(this.luminarPower, selectColourTemperature(this.state), selectLightDistribution(this.state)));
		this.luminarPower = Math.round(this.luminarPower * 10) / 10;

		if (this.lumCount > 1) {
			if (this.luminarPower > selectSolarPowerLimit(state)/this.lumCount) this.luminarPower = selectSolarPowerLimit(state)/this.lumCount;
			//if (this.maxLumenDimmed > selectSolarMaxLumen(state)/this.lumCount) this.maxLumenDimmed = selectSolarMaxLumen(state)/this.lumCount;
			if (this.adjustedLum > selectSolarMaxLumen(state)/this.lumCount) this.adjustedLum = selectSolarMaxLumen(state)/this.lumCount;
		}

		this.efficiency = Math.round(this.adjustedLum/this.luminarPower * 10 ) / 10;
		this.southernHemisphare = state.solar.data?.project?.location?.latitude < 0;
		this.seasonNames = this.southernHemisphare ? {
			winter: {
				de: 'Sommer (max)',
				en: 'Summer (max)',
				fr: 'Été (max)'
			},
			spring: {
				de: 'Früh./Her. (max)',
				en: 'Spring/Aut. (max)',
				fr: 'Prin./Aut. (max)'
			},
			summer: {
				de: 'Winter (max)',
				en: 'Winter (max)',
				fr: 'L\'hiver (max)'
			}
		} : {
			winter: {
				de: 'Winter (max)',
				en: 'Winter (max)',
				fr: 'L\'hiver (max)'
			},
			spring: {
				de: 'Früh./Her. (max)',
				en: 'Spring/Aut. (max)',
				fr: 'Prin./Aut. (max)'
			},
			summer: {
				de: 'Sommer (max)',
				en: 'Summer (max)',
				fr: 'Été (max)'
			}
		};

	}

	firstUpdated() {
		this.plot(false);
	}

	updated() {
		this.plot(false);
	}

	plot(redraw) {
		const plotElement = this.shadowRoot.getElementById('plot');
		if (plotElement && this.plotData) {
			const dimmedPlotData = JSON.parse(JSON.stringify(this.plotData));
			const profile = dimmedPlotData[0].y;
			const dimmedProfile = profile.map(y => y * this.restrictionDimmingFactor);
			const maxValue= Math.max(...dimmedProfile);
			const minValue= Math.min(...dimmedProfile);
			dimmedPlotData[0].y = dimmedProfile;

			// console.log('plot', {profile, dimmedProfile, factor: this.restrictionDimmingFactor, maxValue, minValue});

			// dimming due to PV capacity and battery capacity
			Plotly.newPlot(plotElement,
				dimmedPlotData,
				this.plotLayout,
				this.plotOptions
			);
		}
	}

	render() {
		const label = this.title === '' ? this.seasonNames[this.season][this.lang] : this.title;

		return html`
			<div class="solarDimmProfile">
				<div class="values">
					<span class="label">${label}</span><br>
					${when(this.luminarPower > 0,
						() => html`
							${msg('Power')}: ${this.lumCount > 1 ? this.lumCount + 'x' : ''} ${this.luminarPower} W<br>
							${msg('Lumen')}: ${this.lumCount > 1 ? this.lumCount + 'x' : ''} ${this.adjustedLum} lm<br>
							<!-- ${msg('Eff.')}: ${this.lumCount > 1 ? this.lumCount + 'x' : ''} ${this.efficiency} lm/W<br>-->
						`,
						() => html`
							${msg('Power')}: - W<br>
							${msg('Lumen')}: - lm<br>
						`)}
				</div>
				<div class="plot" id="plot"></div>
			</div>
			<div class="debug ${classMap({
				visible: this.debug,
				overflow: this.restrictionDimmingFactor < 1,
				warn: this.usedInputFactor < 1 && this.usedInputFactor > 0.70
			})}">
				req.: ${Math.round(this.powerRequirement)} Wh,  usable: ${this.usableInput}<br>
				used: ${Math.round(this.usedInputFactor * 100)} % -> dimming level: ${Math.round(this.restrictionDimmingFactor * 100)} %<br>
			</div>
		`;

	}
}
